import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowRight, Check, MessageCircle, Phone, ArrowLeft } from 'lucide-react';
import BlurFade from '../components/primitives/BlurFade';
import Seo from '../components/Seo';
import NotFoundPage from './NotFoundPage';
import CustomPage, { findPublishedCustomPage, RouteLoadingFallback } from './CustomPage';
import { useLang } from '../LangContext';
import { getProduct, getCategory } from '../catalog';
import { company } from '../i18n';
import { mediaAlt } from '../lib/mediaSeo';
import { usePrices, formatPrice, getVolumeTiers } from '../hooks/usePrices';

const telHref = (p) => `tel:${(p || '').replace(/\s/g, '')}`;
const waHref = (msg) => `https://wa.me/${company.whatsapp}?text=${encodeURIComponent(msg || '')}`;

export default function ProductPage() {
  const { lang, overlay, contentReady } = useLang();
  const { category, product } = useParams();
  const location = useLocation();
  const data = getProduct(lang, category, product);
  const prices = usePrices();

  if (!contentReady) return <RouteLoadingFallback />;
  if (findPublishedCustomPage(overlay, location.pathname)) return <CustomPage fallback={null} />;
  if (!data) return <CustomPage fallback={<NotFoundPage />} />;

  const cat = data.category;
  const priceInfo = formatPrice(prices, category, product, lang);
  const insteadLabel = prices?.labels?.instead?.[lang] || (lang === 'de' ? 'statt' : 'in plaats van');
  const volumeTiers = getVolumeTiers(prices, lang);
  const phone = lang === 'de' ? company.phoneDE : company.phoneNL;
  const labels = lang === 'de'
    ? { back: 'Zurück zu', primaryCta: 'Angebot anfragen', wa: 'WhatsApp', call: 'Anrufen', features: 'Eigenschaften', specs: 'Technische Details', otherVariants: 'Andere Varianten', samplePromo: 'Vor dem Kauf gratis Muster anfordern' }
    : { back: 'Terug naar', primaryCta: 'Offerte aanvragen', wa: 'WhatsApp', call: 'Bel ons', features: 'Eigenschappen', specs: 'Specificaties', otherVariants: 'Andere varianten', samplePromo: 'Vraag eerst gratis stalen aan' };

  const otherVariants = cat.variants.filter((v) => v.slug !== product);

  return (
    <article data-testid={`product-${product}`} className="relative bg-[#FAF8F2]">
      <Seo
        title={`${data.name} — ${cat.name}`}
        description={data.summary}
        path={`/${category}/${product}`}
        image={data.image}
        lang={lang}
      />
      {/* Hero — product showcase */}
      <section className="relative pt-28 sm:pt-36 pb-16 lg:pb-24">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-y-10 lg:gap-x-14">
          {/* Left: image */}
          <BlurFade className="lg:col-span-7" offset={20}>
            <div className="relative rounded-[24px] overflow-hidden aspect-[5/4] depth-card bg-white">
              <img src={data.image} alt={mediaAlt(overlay, data.image, lang, data.name)} className="absolute inset-0 w-full h-full object-cover" />
              {data.badge && (
                <span className="absolute top-5 left-5 inline-flex items-center px-3 py-1.5 rounded-full bg-[#F26B1A] text-white text-[11px] uppercase tracking-[0.15em] font-semibold shadow-[0_4px_14px_-4px_rgba(242,107,26,0.55)]">
                  {data.badge}
                </span>
              )}
            </div>
          </BlurFade>

          {/* Right: copy */}
          <div className="lg:col-span-5">
            <BlurFade>
              <nav className="text-[12px] text-[#5C5852] mb-6">
                <Link to="/" className="hover:underline">Home</Link>
                <span className="px-2 opacity-50">/</span>
                <Link to={`/${category}`} className="hover:underline">{cat.name}</Link>
                <span className="px-2 opacity-50">/</span>
                <span className="text-[#1F1D1A]">{data.name}</span>
              </nav>
            </BlurFade>
            <BlurFade delay={0.05}>
              <h1 className="font-display text-[#1F1D1A] text-[36px] sm:text-[48px] lg:text-[60px] leading-[1.05]">{data.name}</h1>
            </BlurFade>
            {data.tagline && (
              <BlurFade delay={0.1}>
                <p className="mt-3 text-[#F26B1A] text-[17px] lg:text-[19px] leading-snug font-light">{data.tagline}</p>
              </BlurFade>
            )}
            {priceInfo && (
              <BlurFade delay={0.15}>
                <div data-testid={`product-${product}-price`} className="mt-6">
                  {priceInfo.isSale && priceInfo.saleLabel && (
                    <span data-testid={`product-${product}-sale-badge`} className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-[#F26B1A] text-white text-[11px] uppercase tracking-[0.18em] font-semibold shadow-[0_4px_14px_-4px_rgba(242,107,26,0.55)]">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-white" />
                      {priceInfo.saleLabel}
                    </span>
                  )}
                  <div className="flex items-baseline gap-4 flex-wrap">
                    <p className={`font-display text-[34px] lg:text-[40px] leading-none ${priceInfo.isSale ? 'text-[#F26B1A]' : 'text-[#1F1D1A]'}`}>
                      {priceInfo.display}
                    </p>
                    {priceInfo.isSale && (
                      <p data-testid={`product-${product}-price-base`} className="text-[#5C5852] text-[16px] lg:text-[18px]">
                        <span className="opacity-70">{insteadLabel}</span>{' '}
                        <span className="line-through">{priceInfo.base}</span>
                      </p>
                    )}
                  </div>
                </div>
              </BlurFade>
            )}
            {volumeTiers && (
              <BlurFade delay={0.18}>
                <div data-testid="volume-tiers" className="mt-5 inline-flex flex-wrap items-center gap-2 max-w-md">
                  <span className="text-[11.5px] uppercase tracking-[0.18em] font-medium text-[#5C5852]">
                    {volumeTiers.label}:
                  </span>
                  {volumeTiers.tiers.map((t) => (
                    <span key={`tier-${t.minQty}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F26B1A]/10 border border-[#F26B1A]/25 text-[11.5px] font-semibold text-[#F26B1A]">
                      {volumeTiers.fromLabel} {t.minQty} {volumeTiers.unit} · −{t.discount}%
                    </span>
                  ))}
                </div>
              </BlurFade>
            )}
            <BlurFade delay={0.2}>
              <p className="mt-5 text-[#5C5852] text-[15.5px] lg:text-[17px] leading-relaxed">{data.summary}</p>
            </BlurFade>

            <BlurFade delay={0.28}>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/offerte" data-testid={`product-${product}-cta`} className="pill pill-leaf">
                  {labels.primaryCta} <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href={waHref(lang === 'de' ? `Hallo, ich interessiere mich für ${data.name}.` : `Hallo, ik wil graag advies over ${data.name}.`)}
                  target="_blank" rel="noreferrer"
                  className="pill pill-outline"
                >
                  <MessageCircle className="w-4 h-4" /> {labels.wa}
                </a>
                <a href={telHref(phone)} className="pill pill-outline">
                  <Phone className="w-4 h-4" /> {labels.call}
                </a>
              </div>
            </BlurFade>

            <BlurFade delay={0.36}>
              <Link to="/gratis-stalen" className="inline-flex items-center gap-2 mt-6 text-[#5C5852] text-[13.5px] hover:text-[#1F1D1A]">
                <Check className="w-3.5 h-3.5 text-[#F26B1A]" /> {labels.samplePromo}
              </Link>
            </BlurFade>
          </div>
        </div>
      </section>

      {/* Features + Specs */}
      <section className="bg-[#F2EEDF] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-y-12 lg:gap-x-16">
          <div className="lg:col-span-7">
            <h2 className="text-[11.5px] uppercase tracking-[0.22em] text-[#F26B1A] font-semibold mb-7">{labels.features}</h2>
            <ul className="space-y-4">
              {data.features.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <span className="mt-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#F26B1A] shrink-0">
                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                  </span>
                  <span className="text-[#1F1D1A] text-[15.5px] leading-relaxed">{f}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5">
            <h2 className="text-[11.5px] uppercase tracking-[0.22em] text-[#F26B1A] font-semibold mb-7">{labels.specs}</h2>
            <dl className="divide-y divide-[#E5E0D2]">
              {Object.entries(data.specs).map(([k, v]) => (
                <div key={k} className="py-3.5 flex items-start justify-between gap-6">
                  <dt className="text-[#5C5852] text-[13.5px] uppercase tracking-wide capitalize">{k}</dt>
                  <dd className="text-[#1F1D1A] text-[14px] text-right max-w-[60%]">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* Other variants in this category */}
      {otherVariants.length > 0 && (
        <section className="bg-[#FAF8F2] py-20 lg:py-28">
          <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
            <div className="flex items-end justify-between mb-10">
              <h2 className="font-display text-[#1F1D1A] text-[32px] sm:text-[40px] lg:text-[52px]">{labels.otherVariants}</h2>
              <Link to={`/${category}`} className="hidden sm:inline-flex items-center gap-1.5 text-[14px] font-medium link-underline">
                <ArrowLeft className="w-3.5 h-3.5" /> {labels.back} {cat.name}
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
              {otherVariants.slice(0, 3).map((v) => {
                const vPrice = formatPrice(prices, category, v.slug, lang);
                return (
                  <Link
                    key={v.slug}
                    to={`/${category}/${v.slug}`}
                    className="group block rounded-[20px] overflow-hidden bg-white border border-[#E5E0D2] hover:-translate-y-1 transition-all duration-500 hover:shadow-[0_24px_50px_-24px_rgba(31,29,26,0.18)]"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden bg-[#F2EEDF]">
                      <img src={v.image} alt={mediaAlt(overlay, v.image, lang, v.name)} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      {vPrice && (
                        <span className={`absolute bottom-3 right-3 inline-flex items-baseline gap-1.5 px-2.5 py-1 rounded-full text-[11.5px] font-semibold ${vPrice.isSale ? 'bg-[#F26B1A] text-white' : 'bg-white/95 text-[#1F1D1A]'}`}>
                          {vPrice.display}
                          {vPrice.isSale && (
                            <span className="line-through opacity-70 text-[10.5px] font-normal">{vPrice.base}</span>
                          )}
                        </span>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="font-display text-[#1F1D1A] text-[20px]">{v.name}</h3>
                      {v.tagline && <p className="text-[13px] text-[#5C5852] mt-1.5 leading-snug">{v.tagline}</p>}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
